/** @format */

import React, { FC, useEffect } from 'react'
import {
  Card,
  Descriptions,
  DescriptionItem,
  Icon,
  Table,
  Copy,
  TableColumn,
  Message,
  Badge
} from '@king-design/react'
import styled from 'styled-components'
import { useParams } from 'react-router'
import { MonitorCrumb } from 'src/components'
import { useAsyncFn, useCopyToClipboard } from 'react-use'
import { getPromethuesInstance } from 'src/api/ApiPromethuesInstance'

// 支持Remote Write的用户ID
const remoteWriteUserIDs: number[] = [73406822, 73401193, 2000159590, 2000170364, 2000179683]

const PrometheusInstance: FC<{ className?: string }> = ({ className }) => {
  const { clusterId } = useParams<{ clusterId: string }>()
  const [copyState, copyToClipboard] = useCopyToClipboard()

  // 获取资源
  const [{ value }, fn] = useAsyncFn(
    async (copy?) => {
      if (clusterId && localStorage.systemId) {
        const result = await getPromethuesInstance({
          SearchRegion: localStorage.systemId,
          ClusterId: clusterId
        })
        if (copy) {
          copyToClipboard(result?.InstancesSet?.[0]?.Token)
        }
        return result?.InstancesSet?.[0] || {}
      } else {
        return {}
      }
    },
    [clusterId]
  )

  // 复制成功/失败后提示
  useEffect(() => {
    const { error, value } = copyState
    if (value) {
      if (error) {
        Message.error('复制失败')
      } else {
        Message.success('复制成功')
      }
    }
  }, [copyState])

  useEffect(() => {
    fn()
  }, [clusterId])

  const onCopyToken = () => {
    fn(true)
  }

  return value ? (
    <div className={className}>
      <MonitorCrumb
        breadArr={[
          { title: 'Prometheus监控' },
          { title: '实例信息', route: '/monitor/prometheusmonitor/instanceinfo' },
          { title: value?.ClusterName || '实例名称' }
        ]}
      />
      <Card title={value?.ClusterName} type="border">
        <Descriptions title="基础信息" className="base_info">
          <DescriptionItem label="实例名称">{value?.ClusterName || '-'}</DescriptionItem>
          <DescriptionItem label="实例ID">{value?.ControllerID || '-'}</DescriptionItem>
          <DescriptionItem label="Region">{value?.RegionCnName || '-'}</DescriptionItem>
          <DescriptionItem label="创建时间">{value?.CreatedAt || '-'}</DescriptionItem>
          <DescriptionItem label="数据存储时长">
            {value?.StorageDuration ? value?.StorageDuration + '天' : '-'}
          </DescriptionItem>
        </Descriptions>
        <Descriptions title="服务地址">
          <DescriptionItem label="token">
            {value?.Token ? (
              <div style={{ paddingTop: 4 }}>
                <span>********</span>
                <Icon className="k-icon-clone" hoverable onClick={onCopyToken} />
              </div>
            ) : (
              '-'
            )}
          </DescriptionItem>
        </Descriptions>
        <p className="sub_titme">Federate地址</p>
        <Table
          checkType="none"
          data={[
            {
              network: '公网',
              url: `https://${value?.Region}.prometheus.ksyun.com/read/${value?.ClusterID}/${value?.UserID}/${value?.ControllerID}/prometheus/federate`
            },
            {
              network: '内网',
              url: `http://${value?.Region}.internal.prometheus.ksyun.com/read/${value?.ClusterID}/${value?.UserID}/${value?.ControllerID}/prometheus/federate`
            }
          ]}
          rowKey={data => data.network}
        >
          <TableColumn key="network" width={100} title="网络" />
          <TableColumn
            key="url"
            title="url"
            slotTemplate={([data]) => {
              return (
                <div className="url_td">
                  <span>{data.url}</span>
                  <Copy text={data?.url}></Copy>
                </div>
              )
            }}
          />
        </Table>
        {remoteWriteUserIDs.includes(value?.UserID) ? (
          <>
            <p className="sub_titme">
              Remote Write地址 <Badge text="Beta版" />
            </p>
            <Table
              checkType="none"
              data={[
                {
                  network: '内网',
                  url: `http://${value?.Region}.internal.prometheus.ksyun.com/write/${value?.ClusterID}/${value?.UserID}/${value?.ControllerID}/prometheus/api/v1/write`
                }
              ]}
              rowKey={data => data.network}
            >
              <TableColumn key="network" width={100} title="网络" />
              <TableColumn
                key="url"
                title="url"
                slotTemplate={([data]) => {
                  return (
                    <div className="url_td">
                      <span>{data.url}</span>
                      <Copy text={data?.url}></Copy>
                    </div>
                  )
                }}
              />
            </Table>
          </>
        ) : null}

        <>
          <p className="sub_titme">HTTP API地址</p>
          <Table
            checkType="none"
            data={[
              {
                network: '公网',
                url: `https://${value?.Region}.prometheus.ksyun.com/read/${value?.ClusterID}/${value?.UserID}/${value?.ControllerID}/prometheus`
              },
              {
                network: '内网',
                url: `http://${value?.Region}.internal.prometheus.ksyun.com/read/${value?.ClusterID}/${value?.UserID}/${value?.ControllerID}/prometheus`
              }
            ]}
            rowKey={data => data.network}
          >
            <TableColumn key="network" width={100} title="网络" />
            <TableColumn
              key="url"
              title="url"
              slotTemplate={([data]) => {
                return (
                  <div className="url_td">
                    <span>{data.url}</span>
                    <Copy text={data?.url}></Copy>
                  </div>
                )
              }}
            />
          </Table>
        </>
      </Card>
    </div>
  ) : null
}

export default styled(PrometheusInstance)`
  .base_info {
    border-bottom: 1px solid rgb(229, 229, 229);
    margin-bottom: 16px;
  }
  .k-icon-clone {
    margin-left: 8px;
  }
  .sub_titme {
    font-size: 14px;
    font-weight: 700;
    line-height: 18px;
    .k-badge {
      margin-top: -2px;
    }
  }
  .k-table {
    margin-bottom: 12px;
  }
  .url_td {
    display: flex;
    align-items: center;
    .k-icon {
      margin-left: 8px;
      cursor: pointer;
    }
  }
`
